import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { RouterLink } from "@angular/router";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { PageEvent } from "@angular/material/paginator";
import { ButtonComponent, PaginatorComponent, PaginatorVariant } from "@sxm-content-creators/tools-ds";
import { EMPTY, Subject, catchError, finalize, startWith, switchMap } from "rxjs";
import { TenantResponse } from "../../../admin/tenants/models/tenant.model";
import { getInteractiveDisplayState } from "../../models/interactive.model";
import { InteractiveListItem, InteractivesListQuery } from "../../models/interactives-list.model";
import {
    STATUS_PILLS,
    StateChangeAction,
    StatusPillValue,
    TYPE_OPTIONS,
    TypeOptionValue
} from "../../models/interactives-list.view";
import { INTERACTIVE_PATCH_BUILDERS } from "../../mappers/interactives-list.mapper";
import { AuthService } from "../../../auth/auth.service";
import { InteractivesApiService } from "../../services/interactives.api.service";
import { InteractiveListResultComponent } from "../../components/interactive-list-result/interactive-list-result.component";
import { TenantsApiService } from "../../../shared/services/tenants.api.service";

@Component({
    selector: "app-interactives",
    templateUrl: "./interactives.component.html",
    styleUrl: "./interactives.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        MatFormFieldModule,
        MatSelectModule,
        ButtonComponent,
        RouterLink,
        InteractiveListResultComponent,
        PaginatorComponent
    ]
})
export class InteractivesComponent implements OnInit {
    private static readonly DEFAULT_PAGE_SIZE = 20;
    private readonly listApi = inject(InteractivesApiService);
    private readonly tenantsApi = inject(TenantsApiService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly auth = inject(AuthService);

    protected readonly isMarketer = computed(() => this.auth.isMarketer());

    readonly items = signal<readonly InteractiveListItem[]>([]);
    readonly isLoading = signal(true);
    readonly tenants = signal<readonly TenantResponse[]>([]);

    readonly selectedStatus = signal<StatusPillValue>("ALL");
    readonly selectedType = signal<TypeOptionValue>("ALL");
    readonly selectedTenantId = signal<string>("ALL");

    readonly statusPills = STATUS_PILLS;
    readonly typeOptions = TYPE_OPTIONS;

    readonly paginatorVariant = PaginatorVariant.CURRENT;
    readonly pageIndex = signal(0);
    readonly pageSize = signal(InteractivesComponent.DEFAULT_PAGE_SIZE);
    readonly totalItems = signal(0);

    // Drives list reloads; switchMap cancels any in-flight request when filters change again.
    private readonly reload$ = new Subject<void>();

    ngOnInit(): void {
        this.reload$
            .pipe(
                startWith(undefined),
                switchMap(() => {
                    this.isLoading.set(true);
                    return this.listApi.getList(this.buildQuery()).pipe(
                        catchError(() => EMPTY),
                        finalize(() => this.isLoading.set(false))
                    );
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(response => {
                this.items.set(response.items);
                this.totalItems.set(response.totalItems);
            });

        this.tenantsApi
            .getAuthorizedTenants()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: tenants => this.tenants.set(tenants),
                error: () => this.tenants.set([])
            });
    }

    selectStatus(value: StatusPillValue): void {
        if (this.selectedStatus() === value) {
            return;
        }
        this.selectedStatus.set(value);
        this.reloadFromFirstPage();
    }

    onTypeChange(value: TypeOptionValue): void {
        this.selectedType.set(value);
        this.reloadFromFirstPage();
    }

    onTenantChange(value: string): void {
        this.selectedTenantId.set(value);
        this.reloadFromFirstPage();
    }

    onPageChange(event: PageEvent): void {
        this.pageIndex.set(event.pageIndex);
        this.pageSize.set(event.pageSize);
        this.reload$.next();
    }

    /** Filter changes reset back to the first page so results aren't shown for an out-of-range page. */
    private reloadFromFirstPage(): void {
        this.pageIndex.set(0);
        this.reload$.next();
    }

    protected onStateChanged(action: StateChangeAction, item: InteractiveListItem): void {
        if (action === "delete" || action === "archive") {
            this.reload$.next();
            return;
        }

        const patch = INTERACTIVE_PATCH_BUILDERS[action](item);
        const status = this.selectedStatus();
        if (status !== "ALL" && getInteractiveDisplayState({ ...item, ...patch }) !== status) {
            this.reload$.next();
            return;
        }

        this.items.update(list => patchItem(list, item.id, patch));
    }

    /** Maps the current pill/dropdown selections to the list query, treating "ALL" as no filter. */
    private buildQuery(): InteractivesListQuery {
        const status = this.selectedStatus();
        const type = this.selectedType();
        const tenantId = this.selectedTenantId();
        return {
            status: status === "ALL" ? null : status,
            type: type === "ALL" ? null : type,
            tenantId: tenantId === "ALL" ? null : tenantId,
            includeStats: true,
            page: this.pageIndex(),
            size: this.pageSize()
        };
    }
}

function patchItem(
    list: readonly InteractiveListItem[],
    id: string,
    patch: Partial<InteractiveListItem>
): InteractiveListItem[] {
    return list.map(item => (item.id === id ? { ...item, ...patch } : item));
}
